<script setup>
import { ref } from 'vue'
import AuthLayout    from './AuthLayout.vue'
import HeadlineFraunces from '@/design/primitives/HeadlineFraunces.vue'
import FieldLeaf     from '@/design/primitives/FieldLeaf.vue'
import ButtonSeed from '@/design/primitives/ButtonSeed.vue'
import { api } from '@/api/client.js'
import { EP }  from '@/api/endpoints.js'

const email   = ref('')
const loading = ref(false)
const sent    = ref(false)

async function handleSubmit() {
  if (!email.value.trim()) return

  loading.value = true
  try {
    await api.post(EP.authForgot, { email: email.value.trim() })
    /* Always show success — avoid email enumeration */
    sent.value = true
  } catch {
    /* Server returns 200 for unknown emails; still show success */
    sent.value = true
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <AuthLayout kicker="EVOLUXU · RECOVER" stamp="A new path opens.">

    <!-- Success state -->
    <template v-if="sent">
      <div class="seal seal--success u-rise-in">
        <p class="seal__label u-display u-allcaps u-gold">INSTRUCTIONS SENT</p>
        <p class="seal__body">Check your inbox — we sent you a reset link.</p>
      </div>
    </template>

    <!-- Form state -->
    <template v-else>
      <HeadlineFraunces level="h2" class="plate__heading">
        Reset <em>access</em>.
      </HeadlineFraunces>

      <form class="seal" @submit.prevent="handleSubmit" novalidate>
        <FieldLeaf
          v-model="email"
          label="EMAIL"
          type="email"
          placeholder="you@example.com"
          required
        />

        <ButtonSeed type="submit" size="lg" :disabled="loading" class="seal__submit">
          {{ loading ? 'SENDING…' : 'SEND LINK' }}
        </ButtonSeed>
      </form>
    </template>

    <!-- Back link — always visible -->
    <RouterLink to="/register?tab=signin" class="plate__back u-display u-allcaps">
      BACK TO SIGN IN
    </RouterLink>

  </AuthLayout>
</template>

<style scoped>
.plate__heading {
  margin-bottom: var(--sp-8);
}

/* ── Form seal ── */
.seal {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
  margin-bottom: var(--sp-6);
}

.seal--success {
  text-align: center;
  padding: var(--sp-8) 0;
}

.seal__label {
  margin: 0 0 var(--sp-3);
  font-size: var(--fs-21);
  letter-spacing: 0.08em;
}

.seal__body {
  margin: 0;
  font-size: var(--fs-16);
  color: var(--ink-soft);
  line-height: var(--lh-body);
}

.seal__submit { width: 100%; }

/* ── Back link ── */
.plate__back {
  display: block;
  margin-top: var(--sp-4);
  text-align: center;
  font-size: var(--fs-12);
  letter-spacing: 0.12em;
  color: var(--gold);
  opacity: 0.75;
  transition: opacity var(--t-fast) var(--ease-road);
}

.plate__back:hover { opacity: 1; }
</style>
